// Lab settings admin page (M7). Loads the single settings row, lets a settings:manage user edit
// case/invoice numbering + lab identity, and saves a minimal patch of changed fields through the
// offline queue. Mirrors RbacMatrix's load / live-refresh / queued-mutate shape; a settings.changed
// SSE (our own replay or another admin's edit) pulls fresh truth into every open tab.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatDate,
  type ClinicMasterStatus,
  type LabSettings,
  type ResetInterval,
  type UpdateSettingsRequest,
  updateSettingsSchema,
  alignerTrayPrice,
  RESET_INTERVALS,
  RESET_INTERVAL_LABELS,
} from '@alignova/shared';
import { api, isDeferred } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { PasswordInput } from '../../ui/PasswordInput.tsx';
import { Button, Card, PageHeader } from '../../ui/primitives.tsx';

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm disabled:opacity-50';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'queued' | 'error';

// Flat, all-string/boolean mirror of the editable settings fields. Nullable identity fields hold
// '' for null so the inputs stay controlled; the patch builder maps '' back to null on save.
interface FormState {
  numberPrefix: string;
  includeYear: boolean;
  resetInterval: ResetInterval;
  invoicePrefix: string;
  labName: string;
  labAddress: string;
  labPhone: string;
  labEmail: string;
  labWhatsapp: string;
  alignerThreshold: string;
  alignerRateAtOrBelow: string;
  alignerRateAbove: string;
}

function toForm(s: LabSettings): FormState {
  return {
    numberPrefix: s.numberPrefix,
    includeYear: s.includeYear,
    resetInterval: s.resetInterval,
    invoicePrefix: s.invoicePrefix,
    labName: s.labName,
    labAddress: s.labAddress ?? '',
    labPhone: s.labPhone ?? '',
    labEmail: s.labEmail ?? '',
    labWhatsapp: s.labWhatsapp ?? '',
    alignerThreshold: String(s.alignerPricing.threshold),
    alignerRateAtOrBelow: String(s.alignerPricing.rateAtOrBelow),
    alignerRateAbove: String(s.alignerPricing.rateAbove),
  };
}

// Number inputs hold strings; a blank or non-numeric field is sent as NaN so the zod schema
// rejects it with a message instead of silently saving 0.
function toInt(v: string): number {
  const t = v.trim();
  return t === '' ? Number.NaN : Number(t);
}

// Only the fields that actually changed vs the loaded row, so a save never clobbers a field a
// concurrent edit touched. Empty nullable text collapses to null.
function buildPatch(form: FormState, base: LabSettings): UpdateSettingsRequest {
  const patch: UpdateSettingsRequest = {};
  if (form.numberPrefix !== base.numberPrefix) patch.numberPrefix = form.numberPrefix;
  if (form.includeYear !== base.includeYear) patch.includeYear = form.includeYear;
  if (form.resetInterval !== base.resetInterval) patch.resetInterval = form.resetInterval;
  if (form.invoicePrefix !== base.invoicePrefix) patch.invoicePrefix = form.invoicePrefix;
  if (form.labName !== base.labName) patch.labName = form.labName;
  if (form.labAddress !== (base.labAddress ?? '')) patch.labAddress = form.labAddress.trim() || null;
  if (form.labPhone !== (base.labPhone ?? '')) patch.labPhone = form.labPhone.trim() || null;
  if (form.labEmail !== (base.labEmail ?? '')) patch.labEmail = form.labEmail.trim() || null;
  if (form.labWhatsapp !== (base.labWhatsapp ?? '')) patch.labWhatsapp = form.labWhatsapp.trim() || null;
  if (toInt(form.alignerThreshold) !== base.alignerPricing.threshold) patch.alignerThreshold = toInt(form.alignerThreshold);
  if (toInt(form.alignerRateAtOrBelow) !== base.alignerPricing.rateAtOrBelow) patch.alignerRateAtOrBelow = toInt(form.alignerRateAtOrBelow);
  if (toInt(form.alignerRateAbove) !== base.alignerPricing.rateAbove) patch.alignerRateAbove = toInt(form.alignerRateAbove);
  return patch;
}

export function SettingsPage() {
  const can = useCan();
  const manage = can('settings:manage');
  const [settings, setSettings] = useState<LabSettings | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    api
      .get<LabSettings>('/settings')
      .then((s) => {
        setSettings(s);
        setForm(toForm(s));
      })
      .catch(() => setError(t('settings.loadError')));
  }, []);

  useEffect(reload, [reload]);

  // Another admin saved, or our queued edit replayed — pull fresh truth and reset the form.
  useLiveEvent('settings.changed', () => {
    setStatus('idle');
    reload();
  });

  // Field setter that also clears any stale saved/queued banner as soon as the user edits again.
  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setStatus('idle');
  }, []);

  const dirty = useMemo(
    () => (settings && form ? Object.keys(buildPatch(form, settings)).length > 0 : false),
    [settings, form],
  );

  const previewNumber = useMemo(() => {
    if (!form) return '';
    const year = new Date().getFullYear();
    const yearPart = form.includeYear ? `${year}-` : '';
    return `${form.numberPrefix}-HOUSE-${yearPart}0001`;
  }, [form]);

  // Live preview of the tiers as typed: one course just inside the threshold, one just past it.
  const pricingPreview = useMemo(() => {
    if (!form) return null;
    const pricing = {
      threshold: toInt(form.alignerThreshold),
      rateAtOrBelow: toInt(form.alignerRateAtOrBelow),
      rateAbove: toInt(form.alignerRateAbove),
    };
    if (![pricing.threshold, pricing.rateAtOrBelow, pricing.rateAbove].every((n) => Number.isInteger(n) && n >= 0)) return null;
    const at = Math.max(1, pricing.threshold);
    const above = pricing.threshold + 1;
    const fmt = (n: number) => `${n} trays = PKR ${alignerTrayPrice(n, pricing).totalRupees.toLocaleString()}`;
    return `${fmt(at)} · ${fmt(above)}`;
  }, [form]);

  async function save() {
    if (!settings || !form) return;
    setError(null);
    const parsed = updateSettingsSchema.safeParse(buildPatch(form, settings));
    if (!parsed.success) {
      setStatus('error');
      setError(parsed.error.issues[0]?.message ?? t('settings.invalid'));
      return;
    }
    setStatus('saving');
    try {
      const res = await api.put<LabSettings>('/settings', parsed.data, { queue: true });
      if (isDeferred(res)) {
        setStatus('queued');
      } else {
        setSettings(res);
        setForm(toForm(res));
        setStatus('saved');
      }
    } catch {
      setStatus('error');
      setError(t('settings.saveError'));
    }
  }

  if (!form || !settings) {
    return (
      <div>
        <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />
        {error ? <p className="text-status-urgent-fg">{error}</p> : <p className="text-muted">{t('common.loading')}</p>}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">{t('settings.numbering.title')}</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-x-4 gap-y-3">
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('settings.numbering.numberPrefix')}</label>
                <input
                  className={inputClass}
                  value={form.numberPrefix}
                  onChange={(e) => set('numberPrefix', e.target.value)}
                  disabled={!manage}
                />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('settings.numbering.invoicePrefix')}</label>
                <input
                  className={inputClass}
                  value={form.invoicePrefix}
                  onChange={(e) => set('invoicePrefix', e.target.value)}
                  disabled={!manage}
                />
              </div>
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('settings.numbering.resetInterval')}</label>
              <select
                className={inputClass}
                value={form.resetInterval}
                onChange={(e) => set('resetInterval', e.target.value as ResetInterval)}
                disabled={!manage}
              >
                {RESET_INTERVALS.map((ri) => (
                  <option key={ri} value={ri}>
                    {RESET_INTERVAL_LABELS[ri]}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={form.includeYear}
                onChange={(e) => set('includeYear', e.target.checked)}
                disabled={!manage}
              />
              {t('settings.numbering.includeYear')}
            </label>
            <div className="rounded border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
              <span className="mr-2">{t('settings.numbering.previewLabel')}</span>
              <span className="font-mono text-ink">{previewNumber}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">{t('settings.identity.title')}</h2>
          <div className="space-y-4">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('settings.identity.labName')}</label>
              <input
                className={inputClass}
                value={form.labName}
                onChange={(e) => set('labName', e.target.value)}
                disabled={!manage}
              />
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('settings.identity.labAddress')}</label>
              <textarea
                className={inputClass}
                rows={2}
                value={form.labAddress}
                onChange={(e) => set('labAddress', e.target.value)}
                disabled={!manage}
              />
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-3">
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('settings.identity.labPhone')}</label>
                <input
                  className={inputClass}
                  value={form.labPhone}
                  onChange={(e) => set('labPhone', e.target.value)}
                  disabled={!manage}
                />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('settings.identity.labWhatsapp')}</label>
                <input
                  className={inputClass}
                  value={form.labWhatsapp}
                  onChange={(e) => set('labWhatsapp', e.target.value)}
                  disabled={!manage}
                />
              </div>
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('settings.identity.labEmail')}</label>
              <input
                type="email"
                className={inputClass}
                value={form.labEmail}
                onChange={(e) => set('labEmail', e.target.value)}
                disabled={!manage}
              />
            </div>
          </div>
        </Card>
      </div>

      <Card className="mt-6 p-6">
        <h2 className="mb-1 text-lg font-semibold text-ink">{t('settings.pricing.title')}</h2>
        <p className="mb-4 max-w-[70ch] text-sm leading-relaxed text-muted">{t('settings.pricing.intro')}</p>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm text-muted">{t('settings.pricing.threshold')}</label>
            <input
              type="number"
              min="0"
              max="1000"
              step="1"
              className={`${inputClass} tabnum`}
              value={form.alignerThreshold}
              onChange={(e) => set('alignerThreshold', e.target.value)}
              disabled={!manage}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-muted">{t('settings.pricing.rateAtOrBelow')}</label>
            <input
              type="number"
              min="0"
              step="1"
              className={`${inputClass} tabnum`}
              value={form.alignerRateAtOrBelow}
              onChange={(e) => set('alignerRateAtOrBelow', e.target.value)}
              disabled={!manage}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-muted">{t('settings.pricing.rateAbove')}</label>
            <input
              type="number"
              min="0"
              step="1"
              className={`${inputClass} tabnum`}
              value={form.alignerRateAbove}
              onChange={(e) => set('alignerRateAbove', e.target.value)}
              disabled={!manage}
            />
          </div>
        </div>
        <div className="mt-4 rounded border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
          <span className="mr-2">{t('settings.pricing.previewLabel')}</span>
          <span className="tabnum text-ink">{pricingPreview ?? t('settings.invalid')}</span>
        </div>
        <p className="mt-2 text-xs text-muted">{t('settings.pricing.note')}</p>
      </Card>

      {manage && <ClinicMasterKey />}

      {manage && (
        <div className="mt-6 flex items-center gap-3">
          <Button onClick={save} disabled={!dirty || status === 'saving'}>
            {status === 'saving' ? t('settings.saving') : t('settings.save')}
          </Button>
          {status === 'saved' && <span className="text-xs text-status-progress-fg">{t('settings.saved')}</span>}
          {status === 'queued' && <span className="text-xs text-muted">{t('sync.queued')}</span>}
          {status === 'error' && error && <span className="text-xs text-status-urgent-fg">{error}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * The lab's master key for clinic portals.
 *
 * One password that opens ANY clinic's portal, alongside the password that clinic chose. It exists
 * because staff routinely have to work inside a clinic's portal, and resetting the clinic's own
 * password to get in locks the doctor out of the account they own.
 *
 * The screen never shows an existing key — it is a bcrypt hash and there is nothing to show. It
 * only reports whether one is set, and lets somebody replace or remove it.
 */
function ClinicMasterKey() {
  const [status, setStatus] = useState<ClinicMasterStatus | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<ClinicMasterStatus>('/settings/clinic-master').then(setStatus).catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  const save = async (clear = false) => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const next = await api.post<ClinicMasterStatus>('/settings/clinic-master', { password: clear ? '' : password.trim() });
      setStatus(next);
      setPassword('');
      setMsg(clear ? 'The master key has been removed.' : 'The master key is set. Nobody can read it back - record it now.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-6">
      <h2 className="text-base font-semibold text-ink">Clinic portal master key</h2>
      <p className="mt-1 max-w-[70ch] text-sm leading-relaxed text-muted">
        One password that signs the lab in to <b className="text-ink">any</b> clinic&rsquo;s portal,
        alongside the password that clinic chose for itself. Staff can work inside a clinic&rsquo;s
        portal without resetting - and locking out - the doctor&rsquo;s own login.
      </p>

      <ul className="mt-3 max-w-[70ch] list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted marker:text-line">
        <li>It opens clinic portals only - never a staff, admin or patient account.</li>
        <li>
          Every sign-in that uses it is recorded in the audit log as a master-key sign-in, so a case
          approved by the lab is never mistaken for one the doctor approved.
        </li>
        <li>It is stored one-way. If it is forgotten, set a new one; nobody can read the old one.</li>
      </ul>

      <p className="mt-4 text-sm">
        {status?.set ? (
          <span className="font-medium text-status-progress-fg">
            A master key is set{status.setAt ? ` - since ${formatDate(status.setAt)}` : ''}.
          </span>
        ) : (
          <span className="text-muted">No master key is set. Each clinic is reachable only by its own login.</span>
        )}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PasswordInput
          className="w-full max-w-xs rounded border border-line bg-surface px-3 py-2 text-sm"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          placeholder="At least 12 characters"
        />
        <Button onClick={() => void save()} disabled={busy || password.trim().length < 12}>
          {status?.set ? 'Replace the key' : 'Set the key'}
        </Button>
        {status?.set && (
          <Button variant="ghost" onClick={() => void save(true)} disabled={busy}>
            Remove it
          </Button>
        )}
      </div>

      {err && <p className="mt-2 text-sm text-status-urgent-fg">{err}</p>}
      {msg && <p className="mt-2 text-sm text-status-progress-fg">{msg}</p>}
    </Card>
  );
}
